import { useEffect, useState } from 'react';
import {
  Badge,
  Balance,
  Button,
  Callout,
  KeyValue,
  Modal,
  Money,
  SkeletonRows,
  cn,
  formatDateTime,
  formatDate,
  formatMoney,
} from '@biovet/ui';
import { ApiError, apiGet, type VoucherDetail } from '../lib/api.js';
import { printReport, type ReportTable } from '../lib/print.js';


export function VoucherDetailModal({
  voucherType,
  voucherNo,
  onClose,
  onOpenLedger,
}: {
  voucherType: string | null;
  voucherNo: string | null;
  onClose: () => void;
  onOpenLedger?: (accountCode: string) => void;
}) {
  const [detail, setDetail] = useState<VoucherDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const open = Boolean(voucherType && voucherNo);

  useEffect(() => {
    if (!voucherType || !voucherNo) return;

    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setDetail(null);

    apiGet<VoucherDetail>(
      `/vouchers/${encodeURIComponent(voucherType)}/${encodeURIComponent(voucherNo)}`,
      {},
      controller.signal,
    )
      .then((result) => {
        setDetail(result);
        setLoading(false);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(
          cause instanceof ApiError
            ? cause.full
            : 'Cannot reach the API. Start it with `pnpm api`.',
        );
        setLoading(false);
      });

    return () => controller.abort();
  }, [voucherType, voucherNo]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        <span className="flex items-center gap-2">
          <span className="bb-mono">{voucherNo}</span>
          {detail && <Badge status={detail.status} />}
          {detail?.reversedByNo && <Badge tone="bad">reversed</Badge>}
        </span>
      }
      subtitle={
        detail
          ? `${formatDate(detail.voucherDate)} · ${detail.periodName ?? 'no period'} · ${
              detail.branchName ?? detail.branchCode ?? 'no branch'
            }`
          : voucherType
            ? `${voucherType} voucher`
            : undefined
      }
      actions={
        detail && (
          <Button
            variant="secondary"
            size="sm"
            data-noprint="1"
            onClick={() => printReport(voucherReport(detail))}
          >
            Print
          </Button>
        )
      }
      footer={
        detail && (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-[11.5px] text-[var(--ink-3)]">
              {detail.lineCount} line{detail.lineCount === 1 ? '' : 's'}
              {detail.currency !== 'PKR' && ` · ${detail.currency} at ${detail.fxRate}`}
            </span>
            <span className="flex items-center gap-3 text-[12px]">
              <span className="text-[var(--ink-3)]">Total</span>
              <Money value={detail.totalDebitBase} className="text-[12.5px]" />
              <span className="text-[var(--ink-3)]">Dr</span>
              <Money value={detail.totalCreditBase} className="text-[12.5px]" />
              <span className="text-[var(--ink-3)]">Cr</span>
            </span>
          </div>
        )
      }
    >
      {error && (
        <div className="p-[var(--pad)]">
          <Callout tone="danger" title="Could not load this voucher">
            {error}
          </Callout>
        </div>
      )}

      {loading && (
        <div className="p-[var(--pad)]">
          <SkeletonRows rows={6} cols={4} />
        </div>
      )}

      {detail && (
        <>
          {detail.narration && (
            <p className="m-0 border-b border-[var(--line-2)] px-[var(--pad)] py-2.5 text-[12.5px] text-[var(--ink-2)]">
              {detail.narration}
            </p>
          )}

          {(detail.reversedByNo || detail.reversalOfNo) && (
            <div className="px-[var(--pad)] pt-3">
              <Callout
                tone={detail.reversedByNo ? 'warn' : 'info'}
                title={
                  detail.reversedByNo ? 'This voucher has been reversed' : 'This is a reversal'
                }
              >
                {detail.reversedByNo
                  ? `${detail.reversedByNo} cancels it out. The lines below stay in the ledger — it is append-only, so a correction is a new entry rather than an edit.`
                  : `It reverses ${detail.reversalOfNo}.`}
                {detail.reversalReason ? ` Reason: ${detail.reversalReason}` : ''}
              </Callout>
            </div>
          )}

          <table className="w-full border-collapse" aria-label={`Lines of ${detail.voucherNo}`}>
            <thead>
              <tr className="bg-[var(--surface-2)]">
                <Th width="34px">#</Th>
                <Th>Account</Th>
                <Th>Particulars</Th>
                <Th width="140px" align="right">
                  Debit
                </Th>
                <Th width="140px" align="right">
                  Credit
                </Th>
              </tr>
            </thead>
            <tbody>
              {detail.lines.map((line) => (
                <tr key={line.lineNo} className="border-b border-[var(--line-2)] align-top">
                  <td className="bb-mono px-3 py-2 text-[11.5px] text-[var(--ink-3)]">
                    {line.lineNo}
                  </td>
                  <td className="px-2 py-2">
                    {}
                    {line.parentCode && (
                      <span className="block text-[11px] text-[var(--ink-3)]">
                        {line.parentCode} {line.parentName}
                      </span>
                    )}
                    {onOpenLedger ? (
                      <button
                        type="button"
                        onClick={() => onOpenLedger(line.accountCode)}
                        className={cn(
                          'cursor-pointer border-0 bg-transparent p-0 text-left',
                          'text-[12.5px] text-[var(--accent)] hover:underline',
                        )}
                      >
                        <span className="bb-mono">{line.accountCode}</span> {line.accountName}
                      </button>
                    ) : (
                      <span className="text-[12.5px] text-[var(--ink)]">
                        <span className="bb-mono">{line.accountCode}</span> {line.accountName}
                      </span>
                    )}
                    {line.partyName && (
                      <span className="block text-[11px] text-[var(--ink-3)]">
                        {line.partyName}
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-[12px] text-[var(--ink-2)]">
                    {line.narration ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Money value={line.debitBase} dashOnZero />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Money value={line.creditBase} dashOnZero />
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-[var(--surface-2)] font-medium">
              <tr>
                <td />
                <td className="px-2 py-2 text-[12px] text-[var(--ink-2)]" colSpan={2}>
                  Totals — equal by <code className="bb-mono">trg_vouchers_balance</code>
                </td>
                <td className="px-3 py-2 text-right">
                  <Money value={detail.totalDebitBase} />
                </td>
                <td className="px-3 py-2 text-right">
                  <Money value={detail.totalCreditBase} />
                </td>
              </tr>
            </tfoot>
          </table>

          <div className="border-t border-[var(--line)] px-[var(--pad)] py-3">
            <h3 className="m-0 pb-2 text-[11px] font-medium tracking-[0.08em] text-[var(--ink-3)] uppercase">
              Audit trail
            </h3>
            <KeyValue items={auditItems(detail)} />
          </div>
        </>
      )}
    </Modal>
  );
}

function auditItems(detail: VoucherDetail): Array<{ label: string; value: string }> {
  const items: Array<{ label: string; value: string }> = [];

  const stamp = (label: string, at: string | null, by: string | null) => {
    if (!at) return;
    items.push({ label, value: by ? `${by} · ${formatDateTime(at)}` : formatDateTime(at) });
  };

  stamp('Created', detail.createdAt, detail.createdByName);
  stamp('Submitted', detail.submittedAt, detail.submittedByName);
  stamp('Approved', detail.approvedAt, detail.approvedByName);
  stamp('Posted', detail.postedAt, detail.postedByName);
  if (detail.updatedAt && detail.updatedAt !== detail.createdAt) {
    stamp('Last edited', detail.updatedAt, detail.updatedByName);
  }

  items.push({ label: 'Status', value: detail.status.toLowerCase().replace(/_/g, ' ') });
  items.push({
    label: 'Currency',
    value: detail.currency === 'PKR' ? 'PKR' : `${detail.currency} at ${detail.fxRate}`,
  });
  if (detail.partyName) items.push({ label: 'Header party', value: detail.partyName });

  return items;
}

function Th({
  children,
  width,
  align = 'left',
}: {
  children: React.ReactNode;
  width?: string;
  align?: 'left' | 'right';
}) {
  return (
    <th
      scope="col"
      style={width ? { width } : undefined}
      className={cn(
        'border-b border-[var(--line-2)] px-3 py-2',
        'text-[10px] font-medium tracking-[0.08em] whitespace-nowrap text-[var(--ink-3)] uppercase',
        align === 'right' ? 'text-right' : 'text-left',
      )}
    >
      {children}
    </th>
  );
}

function voucherReport(detail: VoucherDetail): ReportTable {
  return {
    title: `${detail.voucherType} — ${detail.voucherNo}`,
    fileName: detail.voucherNo,
    meta: [
      formatDate(detail.voucherDate),
      detail.periodName,
      detail.branchName,
      detail.status,
      detail.currency === 'PKR' ? 'PKR' : `${detail.currency} at ${detail.fxRate}`,
      detail.partyName,
      detail.reversedByNo ? `reversed by ${detail.reversedByNo}` : null,
    ]
      .filter(Boolean)
      .join(' · '),
    columns: [
      { label: '#', width: '32px' },
      { label: 'Account', width: '90px' },
      { label: 'Particulars' },
      { label: 'Debit', align: 'right', width: '116px' },
      { label: 'Credit', align: 'right', width: '116px' },
    ],
    rows: [
      ...detail.lines.map((line) => [
        String(line.lineNo),
        line.accountCode,
        [line.accountName, line.partyName, line.narration].filter(Boolean).join(' · '),
        line.debitBase === '0' ? '' : formatMoney(line.debitBase),
        line.creditBase === '0' ? '' : formatMoney(line.creditBase),
      ]),
      {
        cells: [
          '',
          '',
          'Total',
          formatMoney(detail.totalDebitBase),
          formatMoney(detail.totalCreditBase),
        ],
        total: true,
      },
    ],
    notes: [
      detail.narration ? `Narration: ${detail.narration}` : '',
      detail.postedAt && detail.postedByName
        ? `Posted ${formatDateTime(detail.postedAt)} by ${detail.postedByName}.`
        : '',
      detail.reversalReason ? `Reversal reason: ${detail.reversalReason}` : '',
    ].filter(Boolean),
  };
}
